import {
  faceDetect,
  getVideoTask,
  mutiFaceSwap,
  singleFaceSwap,
  videoFaceSwap,
} from "./../api/index";
import { create } from "zustand";
import type {
  TTemplate,
  TTask,
  TSign,
  TTaskOptions,
  TShareOption,
  TSafariInfo,
  TDownloadAdOption,
  TGroup,
  TSwap,
  TMultiFaceSelectOptions,
  TFaceDetection,
  TReuploadOption,
  TServerDeTectFaceData,
  TSwapObject,
  TServerDeTectData,
  TVideoTemplate,
  TVideoTaskOptions,
} from "@/types";
import {
  APICode,
  BASE_CREDIT_CONSUME,
  FACE_CREDIT_CONSUME,
  FACE_LIMIT,
  FaceDetectRes,
  LOOP_INTERVAL,
  LocalStorageKey,
  MAX_LOOP_QUANTITY,
  MOBILE_THUMBNAIL_WIDTH,
  PC_THUMBNAIL_HEIGHT,
  TYPE_LIMIT_ACCEPT,
  TaskStatus,
} from "@/constants";
import {
  getPresetGroupList,
  getSignUrl,
  getTask,
  postTask,
  putFileTo,
} from "@/api";
import {
  checkFileSize,
  checkImageSize,
  checkType,
  cropImage,
  expandFace,
  getExtension,
  getImgDimensions,
  getRandomId,
  heic2jpeg,
  imageLoader,
  isHeic,
  isTiff,
  selectFile,
  sleep,
  tiff2png,
  zipImg,
} from "@/utils";
import { cloneDeep, isEmpty } from "lodash-es";
import { toast } from "react-toastify";
import { t } from "i18next";
import FTAnalytic from "@/analyze/FTAnalytic";
import { FTSwapAnalyzeObj, PROJECT_TYPE } from "@/constants";
import Analyse from "@/utils/analyse";
import {
  checkTempFace,
  checkUploadVideo,
  fetchSign,
  signSwap,
  signTemp,
  submitFile,
  uploadSwap,
  uploadTemp,
} from "@/utils/commonFn";
import { getResizeImage } from "@/utils/link";
import { customConfirm } from "@/components/confirm/modal";
import { faceToSwap, getMaxFaceAndCrop } from "@/utils/faces";
import useUserStore from "./useUserStore";
import { checkVideo, downloadVideoByUrl, VideoError } from "@/utils/video";
import { putHistoryToolUse } from "@/utils/FTHistoryTool";

const VideoProcessBase = {
  start: 5,
  checkFace: 30,
  checkVideo: 50,
  submit: 80,
};

export enum FeatureType {
  /**单人换脸 */
  Single = "image",
  /**多人换脸 */
  Multi = "multiple",
  /** 视频换脸 */
  Video = "video",
}
export const enum GeneratVideoType {
  preview = "preview",
  full = "full",
}
export const enum PageType {
  Ready = "ready",
  Create = "create",
  Generating = "generating",
  Result = "result",
  Failed = "failed",
}
export type TVideoParams = {
  type: GeneratVideoType;
  method: "free" | "credits";
};

type States = {
  mutiLoading: boolean;
  guidTip: boolean;
  presetGroupList: TGroup[];
  presetMutiGroupList: TGroup[];
  presetVideoGroupList: TGroup[];
  activeGroup: TGroup | null;
  activeMutiGroup: TGroup | null;
  /** 单人换脸，人脸数据 */
  swapTemp: TTemplate | null;
  /** 多人换脸，人脸和模板数据 */
  swapList: TSwap[];
  /** 视频换脸，人脸数据 */
  swapVideoTemp: TTemplate | null;
  /** 要被替换的模板图片 */
  activeTemp: TTemplate | null;
  /** 多人，要被替换的原模板 */
  activeMultiTemp: TTemplate | null;
  /** 要被替换的模板视频 */
  videoTemp: TVideoTemplate | null;
  allPresetVisible: boolean;
  /** 单人换脸结果 */
  taskSigle: TTask | null;
  /** 多人换脸结果 */
  taskMuti: TTask | null;
  /** 视频换脸结果 */
  taskVideo: TTask | null;
  generating: boolean;
  isMobile: boolean | undefined;
  removeTipVisible: boolean;
  shareOption: TShareOption;
  safariInfo: TSafariInfo | null;
  downloadAdOption: TDownloadAdOption;
  adLoaded: boolean;
  multiFaceSelectOptions: TMultiFaceSelectOptions;
  isDrag: boolean;
  globalLoading: boolean;
  reuploadOption: TReuploadOption;
  errorMsg: string;
  errorCode: string;
  errorDesc: string;

  // 补充
  pageType: PageType;
  featureType: FeatureType;
  /** 视频按钮生成类型 */
  generatVideoType: GeneratVideoType;
  /** 已经生成过的视频类型 */
  generatedVideoType?: GeneratVideoType;
  // 视频生成进度
  generatVideoProgress: number;
  // 视频解析loading
  videoParceLoading?: boolean;
};

type Getters = {
  getters: {
    /** 当前根据featureType对象的人脸数据：单人和视频 */
    activefaceTemp: TTemplate | null;
    activeBaseTemp: TTemplate | null;
    task: TTask | null;
    recommendTempList: TTemplate[];
    activeTempUrl?: string;
    isResult: boolean;
    resultUrl: string;
    thumUrl: string;
    fillSwapList: TSwap[];
    disabledGenerate: boolean;
    swapCheckFaceing: boolean;
    creditConsume: number;
    isSuccess: boolean;
    isFailed: boolean;
    isLimitFace: boolean;
    isShortVideo: boolean;
    /** 是否优先使用剩余次数 */
    isUseRemains: boolean;
  };
};

type Mutations = {
  setMutiLoading: (value: boolean) => void;
  setGuidTipShow: (value: boolean) => void;
  setTask: (value: TTask | null) => void;
  setGenerating: (value: boolean) => void;
  setIsMobile: (value: boolean | undefined) => void;
  setRemoveTipVisible: (value: boolean) => void;
  setShareOption: (value: TShareOption) => void;
  setSafariInfo: (value: TSafariInfo) => void;
  setDownloadAdOption: (value: TDownloadAdOption) => void;
  setAdLoaded: (value: boolean) => void;
  setAllPresetVisible: (value: boolean) => void;
  setActiveMutiGroup: (value: TGroup) => void;
  setActiveGroup: (value: TGroup) => void;
  setMultiFaceSelectOptions: (value: TMultiFaceSelectOptions) => void;
  setIsDrag: (value: boolean) => void;
  setGlobalLoading: (value: boolean) => void;
  setReuploadOption: (value: TReuploadOption) => void;
  setFeatureType: (value: FeatureType) => void;
  setPageType: (value: PageType) => void;
  setCommonError: (code: string) => void;
  setVoliError: (code: string) => void;
  setVideoTemp: (value: TVideoTemplate | null) => void;
  setActiveFaceTemp: (value: TTemplate | null) => void;
  setActiveBaseTemp: (value: TTemplate | null) => void;
};

type Actions = {
  reset: () => void;
  fetchPresetGroupList: () => Promise<void>;
  fetchMutiPresetGroupList: () => Promise<void>;
  fetchVideoPresetGroupList: () => Promise<void>;
  fetchPresetGroupListByFeatureType: (isReset?: boolean) => Promise<void>;
  submitTask: (options: any) => Promise<TTask>;
  fetchTask: () => Promise<TTask>;
  generateImg: () => Promise<void>;
  generateVideo: () => Promise<void>;
  beforeOperate: (callback?: Function) => void;
  loopTask: () => Promise<void>;
  showRemoveTip: () => void;
  logoutCallback: () => void;
  preApplyPresetTemp: () => void;
  applyPresetTemp: () => void;
  showDownloadAd: () => void;
  chooseTemp: (temp: TTemplate | string | File) => Promise<void>;
  chooseVideoTemp: (temp: TTemplate | File) => Promise<void>;
  chooseSwapTemp: (temp: TTemplate | string | File) => void;
  clearActiveTemp: () => void;
  clearTask: () => void;
  clearSwapTemp: () => void;
  chooseTarget: (swap: TSwap) => Promise<void>;
  clearTarget: (swap: TSwap) => void;
  errorFn: () => void;
  uploadFaceImg: () => Promise<any>;
  checkFaceImg: () => Promise<any>;
  startGenerate: () => Promise<void>;
};

const initialState: States = {
  errorMsg: "",
  mutiLoading: false,
  guidTip: false,
  presetGroupList: [],
  presetMutiGroupList: [],
  presetVideoGroupList: [],
  activeGroup: null,
  activeMutiGroup: null,
  activeTemp: null,
  activeMultiTemp: null,
  swapTemp: null,
  swapVideoTemp: null,
  videoTemp: null,
  swapList: [],
  allPresetVisible: false,
  taskSigle: null,
  taskMuti: null,
  taskVideo: null,
  generating: false,
  isMobile: undefined,
  removeTipVisible: false,
  shareOption: {
    show: false,
  },
  safariInfo: null,
  downloadAdOption: {
    show: false,
  },
  adLoaded: false,
  multiFaceSelectOptions: {
    show: false,
  },
  isDrag: false,
  globalLoading: false,
  reuploadOption: {
    show: false,
  },
  pageType: PageType.Ready,
  featureType: FeatureType.Single,
  errorCode: "",
  errorDesc: "",
  generatVideoType: GeneratVideoType.preview,
  generatVideoProgress: 0,
};

/** 定义一个任务中断控制 */
let needBreak = false;
const getNeedBreak = () => {
  if (needBreak) {
    needBreak = false;
    return true;
  }
};
const setNeedBreak = () => {
  needBreak = true;
};

const useCommonStore = create<States & Getters & Mutations & Actions>(
  (set, get) => ({
    // states
    ...initialState,

    // getters
    getters: {
      get activefaceTemp() {
        const { featureType, swapTemp, swapVideoTemp } = get();
        if (featureType === FeatureType.Single) {
          return swapTemp;
        } else if (featureType === FeatureType.Video) {
          return swapVideoTemp;
        }
        return null;
      },
      get activeBaseTemp() {
        if (get().featureType === FeatureType.Multi) {
          return get().activeMultiTemp;
        }
        return get().activeTemp;
      },
      get task() {
        const { featureType, taskSigle, taskMuti, taskVideo } = get();
        if (featureType === FeatureType.Single) {
          return taskSigle;
        } else if (featureType === FeatureType.Multi) {
          return taskMuti;
        } else if (featureType === FeatureType.Video) {
          return taskVideo;
        }
        return null;
      },
      get recommendTempList() {
        const {
          presetGroupList,
          featureType,
          presetMutiGroupList,
          presetVideoGroupList,
        } = get();
        const isSingle = featureType === FeatureType.Single;
        const isMuti = featureType === FeatureType.Multi;
        const isVideo = featureType === FeatureType.Video;
        const list = isSingle
          ? presetGroupList
          : isVideo
          ? presetVideoGroupList
          : presetMutiGroupList;
        const res: TTemplate[] = [];
        list.forEach((item) => {
          if (isMuti || isVideo) {
            item.tempList && res.push(...item.tempList.slice(0, 3));
          } else {
            if (!isEmpty(item.tempList)) {
              res.push(item.tempList[0]); // 取每组第一个
            }
          }
        });
        return res;
      },
      get activeTempUrl() {
        const { activeBaseTemp } = get().getters;
        return activeBaseTemp?.resource.url || activeBaseTemp?.resource.blobUrl;
      },
      get isResult() {
        const { task } = get().getters;
        if (task === null) {
          return false;
        }
        return [
          TaskStatus.Failed,
          TaskStatus.FeTimeout,
          TaskStatus.Success,
          TaskStatus.Timeout,
        ].includes(task.status);
      },
      get resultUrl() {
        const { task } = get().getters;
        if (task?.status === TaskStatus.Success) {
          return task.videoPath || task.pictureUrl || "";
        }
        return "";
      },
      get thumUrl() {
        const { task } = get().getters;
        if (task?.status === TaskStatus.Success) {
          return task.coverPath || task.pictureUrl || "";
        }
        return "";
      },
      get fillSwapList() {
        const { swapList } = get();
        return swapList.filter((item) => item.source && item.target);
      },
      get swapCheckFaceing() {
        const { swapList, featureType, mutiLoading } = get();
        if (featureType === FeatureType.Single) {
          return false;
        }
        if (featureType === FeatureType.Multi) {
          if (mutiLoading) {
            return true;
          }

          const loading = swapList.some((item) => item.loading);
          if (loading) {
            return true;
          }
        }
        return false;
      },
      get disabledGenerate() {
        const {
          swapTemp,
          swapVideoTemp,
          videoTemp,
          featureType,
          swapList,
          getters: { fillSwapList, activeBaseTemp },
        } = get();

        if (featureType === FeatureType.Single) {
          if (!activeBaseTemp || !swapTemp) {
            return true;
          }
          return false;
        } else if (featureType === FeatureType.Multi) {
          if (!activeBaseTemp || fillSwapList.length === 0) {
            return true;
          }
          const loading = swapList.some((item) => item.loading);
          if (loading) {
            return true;
          }
          return false;
        } else if (featureType === FeatureType.Video) {
          if (!videoTemp || !swapVideoTemp) {
            return true;
          }
          return false;
        }

        // if (fillSwapList.length === 0) {
        //   return true;
        // }
        return false;
      },
      get creditConsume() {
        const {
          getters: { fillSwapList },
          videoTemp,
          featureType,
        } = get();
        const swapLen = fillSwapList?.length;
        const videoDuration = videoTemp?.videoMeta?.duration || 0;

        if (featureType === FeatureType.Single) {
          return BASE_CREDIT_CONSUME;
        } else if (featureType === FeatureType.Multi && swapLen > 0) {
          return BASE_CREDIT_CONSUME + FACE_CREDIT_CONSUME * (swapLen - 1);
        } else if (featureType === FeatureType.Video && videoDuration) {
          // 向上取整，一秒一积分
          return Math.ceil(videoDuration) * 1;
        }
        return 0;
      },
      get isSuccess() {
        const { task } = get().getters;
        if (!task) {
          return false;
        }
        return (
          task.status === TaskStatus.Success && task.whetherViolations === false
        );
      },
      get isFailed() {
        const { task } = get().getters;
        if (!task) {
          return false;
        }
        return (
          [
            TaskStatus.Failed,
            TaskStatus.Timeout,
            TaskStatus.FeTimeout,
          ].includes(task.status) || task.whetherViolations === true
        );
      },
      get isLimitFace() {
        const {
          getters: { fillSwapList },
        } = get();
        return fillSwapList.length >= FACE_LIMIT;
      },
      get isShortVideo() {
        const { videoTemp } = get();
        return (
          typeof videoTemp?.videoMeta?.duration === "number" &&
          videoTemp?.videoMeta?.duration <= 3
        );
      },
      get isUseRemains() {
        const { isLogin, currentRemainNums } = useUserStore.getState().getters;
        const { featureType, generatVideoType } = get();
        const isShortVideo = get().getters.isShortVideo;
        const isVideo = featureType === FeatureType.Video;
        if (!isLogin) {
          if (isVideo) {
            if (generatVideoType === GeneratVideoType.preview) {
              return true;
            } else {
              // 全部视频，如果是短视频，优先使用剩余次数
              if (isShortVideo) {
                return currentRemainNums > 0;
              }
              // 全部视频，其他情况，使用积分
              return false;
            }
          }
          return true;
        }
        // 登录状态下，视频单独处理
        if (isVideo) {
          if (generatVideoType === GeneratVideoType.preview) {
            return true;
          }
          if (isShortVideo) {
            return currentRemainNums > 0;
          }
        }
        // 登录后，其他情况，使用积分
        return false;
      },
    },
    setCommonError: (code: string) => {
      set({
        errorCode: code,
        errorDesc: t("error_result_common_desc"),
        errorMsg: t("error_result_common_title"),
      });
    },
    setVoliError: (value) =>
      set({
        errorCode: value,
        errorDesc: t("error_result_vote_desc"),
        errorMsg: t("error_result_vote_title"),
      }),
    setMutiLoading: (value) => set({ mutiLoading: value }),
    setGuidTipShow: (value) => set({ guidTip: value }),
    setPageType: (value) => set({ pageType: value }),
    setFeatureType: (value) => set({ featureType: value }),
    // mutations
    setTask: (value) => {
      const { featureType } = get();
      if (featureType === FeatureType.Single) {
        set({ taskSigle: value });
      } else if (featureType === FeatureType.Multi) {
        set({ taskMuti: value });
      } else if (featureType === FeatureType.Video) {
        set({ taskVideo: value });
      }
    },
    setGenerating: (value) => set({ generating: value }),
    setIsMobile: (value) => set({ isMobile: value }),
    setRemoveTipVisible: (value) => set({ removeTipVisible: value }),
    setShareOption: (value) => set({ shareOption: value }),
    setSafariInfo: (value) => set({ safariInfo: value }),
    setDownloadAdOption: (value) => set({ downloadAdOption: value }),
    setAdLoaded: (value) => set({ adLoaded: value }),
    setAllPresetVisible: (value) => set({ allPresetVisible: value }),
    setActiveGroup: (value) => set({ activeGroup: value }),
    setActiveMutiGroup: (value) => set({ activeMutiGroup: value }),
    setMultiFaceSelectOptions: (value) =>
      set({ multiFaceSelectOptions: value }),
    setIsDrag: (value) => set({ isDrag: value }),
    setGlobalLoading: (value) => set({ globalLoading: value }),
    setReuploadOption: (value) => set({ reuploadOption: value }),
    setVideoTemp: (value) =>
      set({
        videoTemp: value,
        generatedVideoType: undefined,
      }),
    /** 单人和视频人脸设置 */
    setActiveFaceTemp: (value) => {
      const { featureType } = get();
      if (featureType === FeatureType.Single) {
        set({ swapTemp: value });
      } else if (featureType === FeatureType.Video) {
        set({ swapVideoTemp: value });
      }
    },
    setActiveBaseTemp: (value) => {
      const { featureType } = get();
      if (featureType === FeatureType.Multi) {
        set({ activeMultiTemp: value });
      } else {
        set({ activeTemp: value });
      }
    },

    // actions
    reset: () => set(initialState),
    fetchPresetGroupList: () => {
      return new Promise((resolve, reject) => {
        getPresetGroupList()
          .then((res) => {
            const { code, data = [], msg } = res;
            if (code === APICode.Success) {
              const list = data.map((item1: any) => ({
                id: item1.id,
                title: item1.title,
                tempList: item1.styles?.map((item2: any) => ({
                  id: item2.id,
                  thumbnail: getResizeImage(
                    item2.effectImg,
                    get().isMobile
                      ? MOBILE_THUMBNAIL_WIDTH
                      : PC_THUMBNAIL_HEIGHT
                  ),
                  resource: { url: item2.effectImg },
                  tag: item2.tag,
                })),
              }));
              set({
                presetGroupList: list,
                activeGroup: list[0],
              });
              resolve();
            } else {
              reject(new Error(msg));
            }
          })
          .catch(reject);
      });
    },
    fetchMutiPresetGroupList: () => {
      return new Promise<void>((resolve, reject) => {
        getPresetGroupList("multi")
          .then((res) => {
            const { code, data = [], msg } = res;
            if (code === APICode.Success) {
              const list = data.map((item1: any) => ({
                id: item1.id,
                title: item1.title,
                tempList: item1.styles?.map((item2: any) => ({
                  id: item2.id,
                  thumbnail: getResizeImage(
                    item2.effectImg,
                    get().isMobile
                      ? MOBILE_THUMBNAIL_WIDTH
                      : PC_THUMBNAIL_HEIGHT
                  ),
                  resource: { url: item2.effectImg },
                  tag: item2.tag,
                })),
              }));
              set({
                presetMutiGroupList: list,
                activeMutiGroup: list[0],
              });
              resolve();
            } else {
              reject(new Error(msg));
            }
          })
          .catch(reject);
      });
    },
    fetchVideoPresetGroupList: () => {
      return new Promise<void>((resolve, reject) => {
        getPresetGroupList("videoSingle")
          .then((res) => {
            const { code, data = [], msg } = res;
            if (code === APICode.Success) {
              const list = data.map((item1: any) => ({
                id: item1.id,
                title: item1.title,
                tempList: item1.styles?.map((item2: any) => ({
                  id: item2.id,
                  thumbnail: getResizeImage(
                    item2.coverImg,
                    get().isMobile
                      ? MOBILE_THUMBNAIL_WIDTH
                      : PC_THUMBNAIL_HEIGHT
                  ),
                  resource: { url: item2.videoUrl },
                  tag: item2.tag,
                })),
              }));
              set({
                presetVideoGroupList: list,
              });
              resolve();
            } else {
              reject(new Error(msg));
            }
          })
          .catch(reject);
      });
    },
    /**
     * 获取预设数据，必要时重置
     * @param isReset
     * @returns
     */
    fetchPresetGroupListByFeatureType: (isReset?: boolean) => {
      const {
        featureType,
        presetGroupList,
        presetMutiGroupList,
        presetVideoGroupList,
        fetchPresetGroupList,
        fetchMutiPresetGroupList,
        fetchVideoPresetGroupList,
      } = get();
      if (featureType === FeatureType.Single) {
        if (isReset || presetGroupList.length === 0) {
          return fetchPresetGroupList();
        }
      } else if (featureType === FeatureType.Multi) {
        if (isReset || presetMutiGroupList.length === 0) {
          return fetchMutiPresetGroupList();
        }
      } else if (featureType === FeatureType.Video) {
        if (isReset || presetVideoGroupList.length === 0) {
          return fetchVideoPresetGroupList();
        }
      }
      return Promise.resolve();
    },
    submitTask: (options) => {
      const { featureType } = get();
      return new Promise((resolve, reject) => {
        let functionFn: any = singleFaceSwap;
        if (featureType === FeatureType.Multi) {
          functionFn = mutiFaceSwap;
        } else if (featureType === FeatureType.Video) {
          functionFn = videoFaceSwap;
        }

        Analyse.getInstance().startGererate();

        functionFn(options)
          .then((res: any) => {
            const { code, data, msg } = res;
            if (code === APICode.Success) {
              // 兼容视频换脸数据结构
              resolve(data[0] || data);
            } else {
              let message = "";
              switch (code) {
                case "101":
                  message = "error_no_face";
                  break;
                case "999":
                //  非法参数（预览视频只能走免费通道）
                case "805":
                  message = "error_server";
                  break;
                case "510":
                  message = "error_no_credits";
                  break;
                case "102":
                  message = "error_img_violation";
                  break;
                case "603":
                  message = "error_video_violation";
                  break;
                case "513":
                  message = "error_no_credits";
                  break;
                case "515":
                  // message = "error_no_nums_free";
                  message = "error_no_nums_vip";
                  break;
                default:
                  message = msg;
                  break;
              }
              // 埋点
              Analyse.getInstance().startGererateError(false, message);
              const error = new Error(message);
              error.name = code;
              reject(error);
            }
          })
          .catch((err: any) => {
            // 埋点
            Analyse.getInstance().startGererateError(
              false,
              `submit network failed|${err?.message}|${err?.response?.status}`
            );
            reject(err);
          });
      });
    },
    fetchTask: () => {
      const { featureType } = get();
      const { task } = get().getters;
      const apiFn = featureType === FeatureType.Video ? getVideoTask : getTask;

      return new Promise((resolve, reject) => {
        apiFn(task!.taskId)
          .then((res) => {
            const { code, data, msg } = res;
            if (code === APICode.Success) {
              resolve(data);
            } else {
              reject(new Error(msg));
            }
          })
          .catch(reject);
      });
    },
    errorFn: () => {
      set({
        pageType: PageType.Create,
        generating: false,
      });
    },
    startGenerate: async () => {
      if (get().generating) {
        toast(t("generating"));
        return;
      }
      set({ generating: true });

      const canGenerate = await useUserStore
        .getState()
        .checkCanGenerate()
        .catch(() => false);
      if (!canGenerate) {
        set({ generating: false });
        return;
      }
      // 开始生成
      try {
        if (get().featureType === FeatureType.Video) {
          await get().generateVideo();
        } else {
          await get().generateImg();
        }
      } catch (err) {
        console.warn("generate error", err);
        // 失败的统一处理
        if (get().pageType !== PageType.Failed) {
          if (err === "user_cancel") {
            // 用户取消 不展示错误提示
          } else if (err instanceof Error) {
            const name = err.name;
            if (["101", "102", "513", "515", "603"].includes(name)) {
              toast(t(err.message));
            } else {
              toast(t("generate_failed"));
            }
          } else {
            toast(t("generate_failed"));
          }
          set({
            generating: false,
            pageType: PageType.Create,
          });
        }
      }
    },
    generateImg: async () => {
      try {
        const { swapList, featureType } = get();
        const { activeBaseTemp } = get().getters;
        const { submitTask, errorFn } = get();

        set({ generating: true, pageType: PageType.Generating });
        get().setTask(null);

        // 模板签名
        let newActiveTemp;
        try {
          newActiveTemp = await signTemp(activeBaseTemp!);
        } catch (err) {
          // 埋点
          Analyse.getInstance().startGererateError(
            false,
            "target_image_sign_failed"
          );
          set({
            reuploadOption: { show: true, title: t("error_server") },
          });
          throw err;
        }

        // 模板上传
        try {
          newActiveTemp = await uploadTemp(newActiveTemp);
        } catch (err) {
          // 埋点
          Analyse.getInstance().startGererateError(
            false,
            "target_image_upload_failed"
          );
          set({
            reuploadOption: { show: true, title: t("error_server") },
          });
          errorFn();
          throw err;
        }

        if (featureType === FeatureType.Single) {
          // 检测模板人脸
          try {
            const faces = await checkTempFace(newActiveTemp);
            if (faces.length === 0) {
              set({
                reuploadOption: { show: true, title: t("error_no_face") },
              });
              get().setActiveBaseTemp(null);

              errorFn();
              Analyse.getInstance().startGererateError(
                false,
                "target_image_no_face"
              );
              return;
            }
            if (faces.length > 1) {
              const isToMuti = await customConfirm({
                title: t("confirm_to_muti_title"),
                okText: t("confirm_to_muti_btn_ok"),
                cancelText: t("confirm_to_muti_btn_cancel"),
              });
              Analyse.getInstance().startGererateError(
                false,
                "target_image_face_more_one"
              );
              if (isToMuti) {
                get().setFeatureType(FeatureType.Multi);
                get().setPageType(PageType.Create);
                // 将模板人脸数据传递给多人换脸
                set({
                  activeMultiTemp: cloneDeep(newActiveTemp),
                  mutiLoading: true,
                  generating: false,
                });
                const swapList: TSwap[] = await faceToSwap(
                  faces,
                  newActiveTemp?.resource?.url ?? ""
                );
                set({ mutiLoading: false, swapList: swapList });
                errorFn();
                return;
              } else {
                get().setActiveBaseTemp(null);

                errorFn();
                return;
              }
            }
          } catch (err) {
            // 埋点
            Analyse.getInstance().startGererateError(
              false,
              "template:check face failed"
            );
            errorFn();
            throw err;
          }

          // 检测目标人脸，如果是多人，则弹窗选择
          await get().checkFaceImg();
        } else if (featureType === FeatureType.Multi) {
          // 模板头像签名
          let newSwapList;
          try {
            newSwapList = await Promise.all(
              swapList.map((item) => signSwap(item, "source"))
            );
          } catch (err) {
            Analyse.getInstance().startGererateError(
              false,
              "face_image_sign_failed"
            );
            set({
              reuploadOption: { show: true, title: t("error_server") },
            });
            throw err;
          }
          // 模板头像上传
          try {
            newSwapList = await Promise.all(
              newSwapList.map((item) => uploadSwap(item, "source"))
            );
          } catch (err) {
            // 埋点
            Analyse.getInstance().startGererateError(
              false,
              "face_image_upload_failed"
            );
            set({
              reuploadOption: { show: true, title: t("error_server") },
            });
            throw err;
          }

          // 目标头像签名
          try {
            newSwapList = await Promise.all(
              newSwapList.map((item) => signSwap(item, "target"))
            );
          } catch (err) {
            // 埋点
            Analyse.getInstance().startGererateError(
              false,
              "face_target_image_sign_failed"
            );
            set({
              reuploadOption: { show: true, title: t("error_server") },
            });
            throw err;
          }

          // 目标头像上传
          try {
            newSwapList = await Promise.all(
              newSwapList.map((item) => uploadSwap(item, "target"))
            );
          } catch (err) {
            // 埋点
            Analyse.getInstance().startGererateError(
              false,
              "face_target_image_upload_failed"
            );
            set({
              reuploadOption: { show: true, title: t("error_server") },
            });
            throw err;
          }
          set({
            swapList: newSwapList,
          });
        }

        // 构建请求参数
        const taskOptions: TTaskOptions = {
          targetImg: {
            url: newActiveTemp?.resource.url!,
            width: activeBaseTemp?.dimension?.width!,
            height: activeBaseTemp?.dimension?.height!,
          },
        } as TTaskOptions;
        if (featureType === FeatureType.Multi) {
          const newSwapList = get().swapList;
          taskOptions.swapImages = newSwapList
            .filter((item) => item.source && item.target)
            .map((item) => ({
              template_image_url: item.source.resource.url!,
              user_image_url: item.target?.resource.url!,
              w: item.source.dimension?.width!,
              h: item.source.dimension?.height!,
              x: item.source.coordinate?.x!,
              y: item.source.coordinate?.y!,
            }));
        } else if (featureType === FeatureType.Single) {
          const activefaceTemp = get().getters.activefaceTemp;
          taskOptions.sourceFaceImg = activefaceTemp?.resource.url;
        }

        // 请求换脸
        let task;
        try {
          get().setTask(null);
          set({
            pageType: PageType.Generating,
            errorCode: "",
            errorDesc: "",
            errorMsg: "",
          });
          task = await submitTask(taskOptions);
        } catch (err: any) {
          set({
            generating: false,
            pageType: PageType.Create,
          });
          // if(err.name === "102"){
          //   get().clearActiveTemp(); // 需求：生成图片违规时，清空模板
          // }

          throw err;
        }

        // 保存
        set({
          pageType: PageType.Generating,
        });
        get().setTask({ ...task, status: TaskStatus.Waiting });
        get().setActiveBaseTemp(newActiveTemp);
      } catch (err) {
        set({ generating: false });
        throw err;
      }

      // 轮询任务
      await get().loopTask();
      get().showRemoveTip();
      // 更新次数和积分
      if (useUserStore.getState().getters.isLogin) {
        useUserStore.getState().fetchCredits();
      } else {
        useUserStore.getState().fetchSingleRemainNums();
        useUserStore.getState().fetchMutiRemainNums();
      }
    },
    generateVideo: async () => {
      const type = get().generatVideoType;
      const method = get().getters.isUseRemains ? "free" : "credits";

      set({
        generating: true,
        pageType: PageType.Generating,
        generatVideoProgress: VideoProcessBase.start,
      });
      get().setTask(null);

      const file = get().videoTemp?.resource.file;
      if (!file) {
        return;
      }

      const isSuccess = await get().checkFaceImg();
      set({ generatVideoProgress: VideoProcessBase.checkFace });
      if (!isSuccess) {
        return;
      }

      const newVideoTemp = await checkUploadVideo(get().videoTemp!);
      set({
        videoTemp: newVideoTemp,
        generatVideoProgress: VideoProcessBase.checkVideo,
      });

      const taskOptions: TVideoTaskOptions = {
        videoUrl: get().videoTemp?.resource.url!,
        faceImg: get().swapVideoTemp?.resource.url!,
        type,
        method,
      };

      // 中断
      if (getNeedBreak()) {
        return;
      }
      // 请求换脸
      set({
        pageType: PageType.Generating,
        errorCode: "",
        errorDesc: "",
        errorMsg: "",
      });
      get().setTask(null);

      const task = await get().submitTask(taskOptions);

      // 中断
      if (getNeedBreak()) {
        return;
      }
      // 保存
      get().setTask({
        ...task,
        status: TaskStatus.Waiting,
        videoMeta: get().videoTemp?.videoMeta,
      });
      set({
        pageType: PageType.Generating,
        generatVideoProgress: VideoProcessBase.submit,
      });

      await get().loopTask();

      // 中断
      if (getNeedBreak()) {
        return;
      }
      // 生成之后，需要自动切换全部视频
      if (type === GeneratVideoType.preview) {
        set({
          generatedVideoType: type,
          // 切换到完整视频，隐藏切换tab
          generatVideoType: GeneratVideoType.full,
        });
      } else {
        // 首次生成全部视频，需要给与用户提示
        const isFirst =
          localStorage.getItem(LocalStorageKey.firstGenerateFullVideo) !== "1";
        if (isFirst) {
          toast(t("creations_cloud_tip"), { autoClose: 5000 });
          localStorage.setItem(LocalStorageKey.firstGenerateFullVideo, "1");
        }
      }

      get().showRemoveTip();
      // 更新次数和积分
      useUserStore.getState().fetchVideoRemainNums();
      if (useUserStore.getState().getters.isLogin) {
        useUserStore.getState().fetchCredits();
      }
    },

    uploadTempImg: async () => {},
    uploadFaceImg: async () => {
      const { errorFn } = get();
      const { activefaceTemp } = get().getters;

      if (!activefaceTemp) {
        return;
      }
      let newSwapTemp;
      try {
        newSwapTemp = await signTemp(activefaceTemp);
        get().setActiveFaceTemp(newSwapTemp);
      } catch (err) {
        Analyse.getInstance().startGererateError(
          false,
          "face_image_sign_failed"
        );
        set({
          reuploadOption: { show: true, title: t("error_server") },
        });
        errorFn();
        return;
      }
      if (!newSwapTemp) {
        return;
      }
      try {
        newSwapTemp = await uploadTemp(newSwapTemp);
        get().setActiveFaceTemp(newSwapTemp);
        return true;
      } catch (err) {
        Analyse.getInstance().startGererateError(
          false,
          "face_image_upload_failed"
        );
        set({
          reuploadOption: { show: true, title: t("error_server") },
        });
        errorFn();
      }
    },
    checkFaceImg: async () => {
      const { errorFn, uploadFaceImg } = get();

      const isSuccess = await uploadFaceImg();
      if (!isSuccess) {
        return;
      }

      try {
        const faces = await checkTempFace(get().getters.activefaceTemp!);
        if (faces.length === 0) {
          set({
            reuploadOption: { show: true, title: t("error_no_face") },
          });
          get().setActiveFaceTemp(null);
          Analyse.getInstance().startGererateError(false, "face_image_no_face");
          errorFn();
          return;
        }
        if (faces.length > 1) {
          Analyse.getInstance().startGererateError(
            false,
            "face_image_face_more_one"
          );
          const { activefaceTemp } = get().getters;
          const newFile = activefaceTemp!.resource.file!;
          const img = await imageLoader(newFile);
          let detections: any[] = faces.map((item) => {
            const [x1, y1, x2, y2] = item.bbox;
            const width = x2 - x1;
            const height = y2 - y1;
            const newFace = expandFace(
              {
                width: img.width,
                height: img.height,
              },
              {
                x: x1,
                y: y1,
              },
              {
                width,
                height,
              }
            );
            return {
              expandBox: {
                x: newFace.coordinate.x,
                y: newFace.coordinate.y,
                width: newFace.dimensions.width,
                height: newFace.dimensions.height,
              },
            };
          });

          const faceDetectionList: TFaceDetection[] = await Promise.all(
            detections.map((detection) =>
              cropImage(
                newFile,
                detection.expandBox.x,
                detection.expandBox.y,
                detection.expandBox.width,
                detection.expandBox.height
              ).then((res) => ({ ...detection, face: res }))
            )
          );

          return new Promise((resolve, reject) => {
            set({
              multiFaceSelectOptions: {
                show: true,
                faceDetectionList,
                onSelect: async (faceDetection) => {
                  Analyse.getInstance().openFaceSwapCallback(true, "");
                  putHistoryToolUse();
                  const { face, expandBox } = faceDetection;

                  const newData = get().getters.activefaceTemp!;
                  const thumb = URL.createObjectURL(face!);
                  // 修改值重新上传
                  newData.resource.url = "";
                  newData.resource.file = face;
                  newData.resource.blobUrl = thumb;
                  // 同时更新回显
                  newData.thumbnail = thumb;
                  get().setActiveFaceTemp(newData);
                  const isSuccess = await get().uploadFaceImg();

                  if (isSuccess) {
                    resolve(true);
                  }
                },
                onClose: () => {
                  reject("user_cancel");
                },
              },
            });
          });
        }
        return true;
      } catch (err) {
        set({
          reuploadOption: { show: true, title: t("error_server") },
        });
      }
    },

    beforeOperate: async (callback) => {
      if (get().generating) {
        toast(t("generating"));
        return;
      }
      callback?.();
    },
    loopTask: async () => {
      const LastProcessBase = VideoProcessBase.submit;
      try {
        let loopQuantity = 0;
        let task = await get().fetchTask();
        while (
          loopQuantity < MAX_LOOP_QUANTITY &&
          [TaskStatus.Waiting, TaskStatus.Generating].includes(task.status) &&
          !getNeedBreak()
        ) {
          await sleep(LOOP_INTERVAL);
          set({
            generatVideoProgress: Math.floor(
              ((task.progress || 0) / 100) * (100 - LastProcessBase) +
                LastProcessBase
            ),
          });
          task = await get().fetchTask();
          loopQuantity += 1;
        }
        // 中断
        if (getNeedBreak()) {
          return;
        }
        // 保留原来的一些数据
        const newTask = {
          ...get().getters.task,
          ...task,
          // 记录当前生成的视频类型
          generatVideoType: get().generatVideoType,
        };
        // 进度更新
        get().setTask(newTask);
        set({
          generating: false,
          generatVideoProgress: Math.floor(
            ((task.progress || 0) / 100) * (100 - LastProcessBase) +
              LastProcessBase
          ),
        });

        if (task.whetherViolations === true) {
          // 埋点（敏感图）
          Analyse.getInstance().generateCallBack(false, "error_img_violation");
          get().setVoliError("102");
          set({ pageType: PageType.Failed });
          return;
        }
        get().setCommonError(task.status + "");
        switch (task.status) {
          case TaskStatus.Success:
            // 埋点（成功）
            Analyse.getInstance().generateCallBack(true, "");

            set({ pageType: PageType.Result });
            break;
          case TaskStatus.Waiting:
          case TaskStatus.Generating:
            task.status = TaskStatus.FeTimeout;
            set({ pageType: PageType.Failed });
            get().setTask(task);

            // 埋点（前端超时）
            Analyse.getInstance().generateCallBack(false, "fonted_timeout");

            break;
          case TaskStatus.Timeout:
            // 埋点（后端超时）
            Analyse.getInstance().generateCallBack(false, "server_timeout");
            set({ pageType: PageType.Failed });
            break;
          case TaskStatus.Failed:
            // 埋点（生成失败）
            Analyse.getInstance().generateCallBack(
              false,
              "task_failed_status:" + task.status
            );
            set({ pageType: PageType.Failed });

            break;
          default:
            // 埋点（未知）
            Analyse.getInstance().generateCallBack(
              false,
              "unknown_status:" + task.status
            );
            set({ pageType: PageType.Failed });
            break;
        }
      } catch (err) {
        console.error(err);
        set({ generating: false });
        Analyse.getInstance().generateCallBack(false, "task error:" + err);

        if (getNeedBreak()) {
          return;
        }

        set({ pageType: PageType.Failed });
      }
    },
    showRemoveTip: () => {
      const removeTipShowTimesStr = localStorage.getItem(
        LocalStorageKey.removeTipShowTimes
      );
      if (removeTipShowTimesStr) {
        const removeTipShowTimeList = removeTipShowTimesStr.split(",");
        if (removeTipShowTimeList.length < 3) {
          const getDateComponents = (timestamp: number) => {
            const date = new Date(timestamp);

            const year = date.getFullYear();
            const month = date.getMonth() + 1; // 月份从0开始，因此需要加1
            const dayOfMonth = date.getDate();

            return {
              year: year,
              month: month,
              dayOfMonth: dayOfMonth,
            };
          };

          const lastTimeStr =
            removeTipShowTimeList[removeTipShowTimeList.length - 1];
          const lastTime = Number(lastTimeStr);
          const now = Date.now();
          const lastTimeComponent = getDateComponents(lastTime);
          const nowTimeComponent = getDateComponents(now);
          if (
            nowTimeComponent.year > lastTimeComponent.year ||
            (nowTimeComponent.year === lastTimeComponent.year &&
              nowTimeComponent.month > lastTimeComponent.month) ||
            (nowTimeComponent.year === lastTimeComponent.year &&
              nowTimeComponent.month === lastTimeComponent.month &&
              nowTimeComponent.dayOfMonth > lastTimeComponent.dayOfMonth)
          ) {
            set({ removeTipVisible: true });
            removeTipShowTimeList.push(String(now));
            localStorage.setItem(
              LocalStorageKey.removeTipShowTimes,
              removeTipShowTimeList.join(",")
            );
          }
        }
      } else {
        set({ removeTipVisible: true });
        localStorage.setItem(
          LocalStorageKey.removeTipShowTimes,
          `${Date.now()}`
        );
      }
    },
    logoutCallback: () => {
      // set({
      //   swapList: [],
      //   generatedVideoType: undefined,
      //   generating: false,
      //   mutiLoading: false,
      //   pageType: PageType.Create,
      // });
      set({ ...initialState });
      get().setTask(null);
      get().setActiveBaseTemp(null);
      get().setActiveFaceTemp(null);
      setNeedBreak();
    },
    preApplyPresetTemp: () => {
      const menu = "type";
      const query = new URLSearchParams(window.location.search);
      const menuValue: any = query.get(menu);
      const isInFeatureType = Object.values(FeatureType).includes(menuValue);
      if (isInFeatureType) {
        get().setFeatureType(menuValue);
        get().setPageType(PageType.Create);
      }
      query.delete(menu);
      let url = window.location.pathname;
      if ((query as any).size > 0) {
        url += `?${query.toString()}`;
      }
      window.history.replaceState(null, "", url);
    },
    // 进入页面时，根据url参数，应用预设模板
    applyPresetTemp: () => {
      const menu = "type";
      const groupIdName = "style";
      const tempIdName = "id";
      const otherGroupIdName = "soureId";
      const faceImgKey = "faceimg";
      const targetImgKey = "targetimg";
      const targetVideoKey = "targetvideo";
      const query = new URLSearchParams(window.location.search);
      const groupId = query.get(groupIdName);
      const tempId = query.get(tempIdName);
      const otherGroupId = query.get(otherGroupIdName);
      const menuValue = query.get(menu);
      const featureType = get().featureType;
      const nowGroupId = groupId || otherGroupId;
      const faceImg = query.get(faceImgKey);
      const targetImg = query.get(targetImgKey);
      const targetVideo = query.get(targetVideoKey);

      const {
        presetGroupList,
        chooseTemp,
        chooseSwapTemp,
        chooseVideoTemp,
        presetMutiGroupList,
      } = get();
      const isSingle = featureType === FeatureType.Single;
      const groupList = isSingle ? presetGroupList : presetMutiGroupList;
      let group, temp;

      group = groupList.find((item) => item.id === nowGroupId);
      groupList.forEach((item1) => {
        const index = item1.tempList.findIndex((item2) => item2.id === tempId);
        if (index > -1) {
          temp = item1.tempList[index];
        }
      });
      // 人脸图片填充
      if (faceImg) {
        chooseSwapTemp(faceImg);
      }
      // 视频填充
      if (targetVideo) {
        chooseVideoTemp({
          id: getRandomId(),
          resource: {
            url: targetVideo,
          },
        });
      }
      if (targetImg) {
        // TODO: 带模板图待完成
      } else if (temp) {
        chooseTemp(temp);
      } else if (group) {
        if (isSingle) {
          set({
            allPresetVisible: true,
            activeGroup: group,
          });
        } else {
          set({
            allPresetVisible: true,
            activeMutiGroup: group,
          });
        }
      }
      // 清空
      query.delete(menu);
      query.delete(groupIdName);
      query.delete(tempIdName);
      query.delete(otherGroupIdName);
      query.delete(faceImgKey);
      query.delete(targetImgKey);
      query.delete(targetVideoKey);
      let url = window.location.pathname;
      if ((query as any).size > 0) {
        url += `?${query.toString()}`;
      }
      window.history.replaceState(null, "", url);
    },
    showDownloadAd: () => {
      let adDatas: any = localStorage.getItem(LocalStorageKey.adData);
      if (adDatas) {
        try {
          adDatas = JSON.parse(adDatas);
          const adData = adDatas.find((item1: any) => {
            if (item1.type === "export") {
              const index = item1.positionRegex.findIndex((item2: any) =>
                new RegExp(item2.regex).test(window.location.pathname)
              );
              return index !== -1;
            }
            return false;
          });
          if (adData) {
            set({
              downloadAdOption: {
                show: true,
                jumpUrl: adData.jumpUrl,
                cover: adData.cover,
                id: adData.id,
              },
            });
          }
        } catch (err) {
          console.error(err);
        }
      }
    },
    /**
     * 新版本选择模板，不再使用前端人脸检测，现在本地转换格式，客户端点击生成后再上传
     * @param temp
     */
    chooseSwapTemp: async (temp) => {
      Analyse.getInstance().openFaceSwap();
      if (!checkType(temp)) {
        Analyse.getInstance().openFaceSwapCallback(false, "img_format_error");
        toast(t("img_format_error"));
        return;
      }
      if (!checkFileSize(temp)) {
        Analyse.getInstance().openFaceSwapCallback(false, "img_size_error");
        toast(t("img_size_error"));
        return;
      }
      const isLg8192 = await checkImageSize(temp);
      if (!isLg8192) {
        Analyse.getInstance().openFaceSwapCallback(
          false,
          "img_max_slider_error"
        );
        toast(t("img_max_slider_error"));
        return;
      }
      let newFile = temp;
      if (temp instanceof File) {
        newFile = await zipImg(temp);
      }
      const isCustom = typeof temp === "string" || temp instanceof File;
      if (isCustom) {
        if (typeof temp === "string") {
          get().setActiveFaceTemp({
            id: getRandomId(),
            thumbnail: temp,
            resource: {
              url: temp,
            },
          });
        } else {
          const blobUrl = URL.createObjectURL(newFile as File);
          get().setActiveFaceTemp({
            id: getRandomId(),
            thumbnail: blobUrl,
            resource: {
              file: newFile as File,
              blobUrl,
            },
          });
        }
      } else {
        get().setActiveFaceTemp({ ...(temp as TTemplate) });
      }
      set({ generatedVideoType: undefined });
      Analyse.getInstance().openFaceSwapCallback(true, "");
      putHistoryToolUse();
    },
    chooseFaceSwapTemp: async (temp: any) => {
      if (!checkType(temp)) {
        toast(t("img_format_error"));
        return;
      }
    },
    chooseVideoTemp: async (temp) => {
      Analyse.getInstance().openTarget();
      let file = temp as File;
      const url = (temp as TTemplate)?.resource?.url;
      set({ videoParceLoading: true });
      if (url) {
        file = await downloadVideoByUrl(url);
      }
      return await checkVideo(file, !!url)
        .then((fileInfo) => {
          console.warn("checkVideo", fileInfo);
          get().setVideoTemp({
            thumbnail: fileInfo.thumb,
            videoMeta: {
              width: fileInfo.width,
              height: fileInfo.height,
              duration: fileInfo.duration,
              frameRate: fileInfo.frameRate,
            },
            resource: {
              file: fileInfo.file,
              blobUrl: fileInfo.url,
              // 如果是模板视频，使用模板视频的url
              url,
            },
          });
          Analyse.getInstance().openTargetCallback(true, "");
          putHistoryToolUse();
          set({ videoParceLoading: false });
          // 如果视频小于3s，默认使用全视频生成
          if (fileInfo.duration <= 3) {
            set({ generatVideoType: GeneratVideoType.full });
          }
        })
        .catch((err) => {
          console.error(err);
          set({ videoParceLoading: false });
          if (Object.values(VideoError).includes(err?.message)) {
            Analyse.getInstance().openTargetCallback(false, err?.message);
            toast(t(err?.message));
          }
        });
    },
    chooseTemp: async (temp) => {
      Analyse.getInstance().openTarget();
      let newFile = temp;
      if (!checkType(temp)) {
        toast(t("img_format_error"));
        Analyse.getInstance().openTargetCallback(false, "img_format_error");
        return;
      }

      if (!checkFileSize(temp)) {
        toast(t("img_size_error"));
        Analyse.getInstance().openTargetCallback(false, "img_size_error");
        return;
      }
      const isLg8192 = await checkImageSize(temp);
      if (!isLg8192) {
        Analyse.getInstance().openTargetCallback(false, "img_max_slider_error");
        toast(t("img_max_slider_error"));
        return;
      }
      const initFeatureType = get().featureType;
      const mutiLoading = get().mutiLoading;
      if (initFeatureType === FeatureType.Multi && mutiLoading) {
        return;
      }
      if (initFeatureType === FeatureType.Multi) {
        set({ mutiLoading: true });
      }
      if (temp instanceof File) {
        newFile = await zipImg(temp);
      }
      const nowFeatureType = get().featureType;
      if (nowFeatureType !== initFeatureType) {
        set({ mutiLoading: false });
        return;
      }
      const isCustom = typeof newFile === "string" || newFile instanceof File;
      try {
        // const detectImg = isCustom ? temp : temp.resource.url!;
        if (isCustom) {
          if (typeof newFile === "string") {
            get().setActiveBaseTemp({
              id: getRandomId(),
              thumbnail: newFile,
              resource: {
                url: newFile,
              },
            });
          } else {
            const blobUrl = URL.createObjectURL(newFile as File);
            console.log(newFile);
            get().setActiveBaseTemp({
              id: getRandomId(),
              thumbnail: blobUrl,
              resource: {
                file: newFile as File,
                blobUrl,
              },
            });
          }
          const nowFeatureType = get().featureType;
          if (nowFeatureType !== initFeatureType) {
            return;
          }
          await imageLoader(newFile as File).then((img) => {
            const { activeBaseTemp } = get().getters;
            const nowFeatureType = get().featureType;
            if (nowFeatureType !== initFeatureType) {
              return;
            }
            if (!activeBaseTemp) {
              return;
            }
            get().setActiveBaseTemp({
              ...activeBaseTemp,
              id: activeBaseTemp?.id ?? getRandomId(),
              dimension: {
                width: img.width,
                height: img.height,
              },
            });
          });
        } else {
          get().setActiveBaseTemp({ ...(newFile as TTemplate) });

          if (newFile && (newFile as TTemplate).resource.url) {
            await imageLoader((newFile as TTemplate).resource.url || "").then(
              (img) => {
                const { activeBaseTemp } = get().getters;
                if (!activeBaseTemp) {
                  return;
                }
                get().setActiveBaseTemp({
                  ...activeBaseTemp,
                  id: activeBaseTemp?.id ?? getRandomId(),
                  dimension: {
                    width: img.width,
                    height: img.height,
                  },
                });
              }
            );
          }
        }
        const nowFeatureType = get().featureType;
        if (nowFeatureType !== initFeatureType) {
          set({ mutiLoading: false });
          return;
        }
        // 如果是多人脸，需要执行一次人脸检测
        if (get().featureType === FeatureType.Multi) {
          // if (mutiLoading) {
          //   return;
          // }

          let newActiveTemp = get().getters.activeBaseTemp;
          if (!newActiveTemp) {
            return;
          }
          get().setMutiLoading(true);
          const end = () => {
            get().setMutiLoading(false);
          };
          try {
            newActiveTemp = await signTemp(newActiveTemp);
          } catch (err) {
            Analyse.getInstance().openTargetCallback(false, "target_sign_fail");
            end();
            return;
          }

          // 模板上传
          try {
            newActiveTemp = await uploadTemp(newActiveTemp);
          } catch (err) {
            Analyse.getInstance().openTargetCallback(
              false,
              "target_upload_fail"
            );
            end();
            return;
          }
          get().setActiveBaseTemp(newActiveTemp);
          // 检测模板人脸
          try {
            const faces = await checkTempFace(newActiveTemp);
            if (faces.length > 0) {
              const swapList: TSwap[] = await faceToSwap(
                faces,
                newActiveTemp?.resource?.url ?? ""
              );
              if (get().getters.activeBaseTemp) {
                set({
                  swapList: swapList,
                });
              } else {
                set({
                  swapList: [],
                });
              }
            } else {
              Analyse.getInstance().openTargetCallback(false, "target_no_face");
              set({
                reuploadOption: { show: true, title: t("error_no_face") },
                swapList: [],
              });
              get().setActiveBaseTemp(null);
            }
            end();
          } catch (err) {
            Analyse.getInstance().openTargetCallback(
              false,
              `unkown_error:${
                typeof err === "string" ? err : (err as Error).message
              }`
            );
            end();
          }
        }
        Analyse.getInstance().openTargetCallback(true, "");
        putHistoryToolUse();
      } catch (err) {
        Analyse.getInstance().openTargetCallback(
          false,
          `unkown_error:${
            typeof err === "string" ? err : (err as Error).message
          }`
        );
        console.error(err);
        set({ globalLoading: false, mutiLoading: false });
      }
    },
    clearTask: () => {
      set({ generatedVideoType: undefined });
      get().setTask(null);
    },
    clearActiveTemp: () => {
      set({
        swapList: [],
      });
      get().setActiveBaseTemp(null);
    },
    clearSwapTemp: () => {
      set({ generatedVideoType: undefined });
      get().setActiveFaceTemp(null);
    },
    chooseTarget: async (swap) => {
      Analyse.getInstance().openFaceSwap();
      let [file] = await selectFile({ accept: TYPE_LIMIT_ACCEPT });

      if (!checkType(file)) {
        toast(t("img_format_error"));
        Analyse.getInstance().openFaceSwapCallback(false, "img_format_error");

        return;
      }

      if (isTiff(file)) {
        file = await tiff2png(file);
      }

      if (isHeic(file)) {
        file = await heic2jpeg(file);
      }

      if (!checkFileSize(file)) {
        toast(t("img_size_error"));
        Analyse.getInstance().openFaceSwapCallback(false, "img_size_error");
        return;
      }

      const isLg8192 = await checkImageSize(file);
      if (!isLg8192) {
        Analyse.getInstance().openFaceSwapCallback(
          false,
          "img_max_slider_error"
        );
        toast(t("img_max_slider_error"));
        return;
      }

      const newFile = await zipImg(file);
      const { swapList } = get();
      const findSwap = swapList.find((item) => item.id === swap.id);
      if (!findSwap) {
        return;
      }
      try {
        findSwap.loading = true;
        set({ swapList: [...swapList] });
        const sign = await fetchSign(getExtension(newFile));
        await submitFile(newFile, sign.uploadUrl || sign.backupUploadUrl || "");
        const img = await imageLoader(newFile);
        let facesResut: TServerDeTectData = await faceDetect(
          sign.downloadUrl || ""
        );
        let code: FaceDetectRes = FaceDetectRes.Unknown;
        let detections: any[] = facesResut.faces.map((item) => {
          const [x1, y1, x2, y2] = item.bbox;
          const width = x2 - x1;
          const height = y2 - y1;
          const newFace = expandFace(
            {
              width: img.width,
              height: img.height,
            },
            {
              x: x1,
              y: y1,
            },
            {
              width,
              height,
            }
          );
          return {
            expandBox: {
              x: newFace.coordinate.x,
              y: newFace.coordinate.y,
              width: newFace.dimensions.width,
              height: newFace.dimensions.height,
            },
          };
        });

        if (facesResut.faces.length === 0) {
          code = FaceDetectRes.NoFace;
        } else if (facesResut.faces.length === 1) {
          code = FaceDetectRes.Success;
        } else if (facesResut.faces.length > 1) {
          code = FaceDetectRes.TooManyFace;
        }
        switch (code) {
          case FaceDetectRes.NoFace:
            findSwap.loading = false;
            Analyse.getInstance().openFaceSwapCallback(
              false,
              "face_image_no_face"
            );
            set({
              swapList: [...swapList],
              reuploadOption: { show: true, title: t("error_no_face") },
            });
            break;
          // case FaceDetectRes.TooSmall:
          //   set({
          //     reuploadOption: { show: true, title: t("face_result_too_small") },
          //   });
          //   break;
          // case FaceDetectRes.TooBig:
          //   set({
          //     reuploadOption: { show: true, title: t("face_result_too_big") },
          //   });
          //   break;
          case FaceDetectRes.Unknown:
            set({ reuploadOption: { show: true, title: t("unknown_error") } });
            break;
          case FaceDetectRes.Success:
            Analyse.getInstance().openFaceSwapCallback(true, "");
            putHistoryToolUse();
            const detection = detections[0];
            const targetHead: File = await cropImage(
              newFile,
              detection.expandBox.x,
              detection.expandBox.y,
              detection.expandBox.width,
              detection.expandBox.height
            );
            findSwap.loading = false;
            findSwap!.target = {
              resource: {
                file: targetHead,
                blobUrl: URL.createObjectURL(targetHead),
              },
              dimension: {
                width: detection.expandBox.width,
                height: detection.expandBox.height,
              },
              coordinate: {
                x: detection.expandBox.x,
                y: detection.expandBox.y,
              },
            };

            set({
              swapList: [...swapList],
            });
            break;
          case FaceDetectRes.TooManyFace:
            findSwap.loading = false;

            const faceDetectionList: TFaceDetection[] = await Promise.all(
              detections.map((detection) =>
                cropImage(
                  newFile,
                  detection.expandBox.x,
                  detection.expandBox.y,
                  detection.expandBox.width,
                  detection.expandBox.height
                ).then((res) => ({ ...detection, face: res }))
              )
            );

            set({
              swapList: [...swapList],
              multiFaceSelectOptions: {
                show: true,
                faceDetectionList,
                onSelect: (faceDetection) => {
                  Analyse.getInstance().openFaceSwapCallback(true, "");
                  putHistoryToolUse();
                  const { face, expandBox } = faceDetection;
                  findSwap!.target = {
                    resource: {
                      file: face,
                      blobUrl: URL.createObjectURL(face!),
                    },
                    dimension: {
                      width: expandBox.width,
                      height: expandBox.height,
                    },
                    coordinate: {
                      x: expandBox.x,
                      y: expandBox.y,
                    },
                  };

                  set({
                    swapList: [...swapList],
                  });
                },
              },
            });
            break;
          default:
            break;
        }
      } catch (err) {
        console.error(err);
        Analyse.getInstance().openFaceSwapCallback(
          false,
          `unkown_error:${
            typeof err === "string" ? err : (err as Error).message
          }`
        );
        set({ globalLoading: false });
        findSwap!.loading = false;
        set({
          swapList: [...swapList],
        });
      }
    },
    clearTarget: (swap: TSwap) => {
      const { swapList } = get();
      const findSwap = swapList.find((item) => item.id === swap.id);
      findSwap!.target = undefined;
      set({ swapList: [...swapList] });
    },
  })
);

export default useCommonStore;
